import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Plus } from "lucide-react";
import PileFormDialog from "./PileFormDialog";
import PileDetailPanel from "./PileDetailPanel";
import { SiteFilterSelect } from "./shared/SiteFilterSelect";
import { NoAccess } from "../equipment/shared/NoAccess";
import { PaginationBar } from "@/components/shared/PaginationBar";

const HO_ROLES = ["Director", "Manager", "Back Office"];

export default function PileRecordTab() {
  const { user } = useAuth();
  const isSE = user?.role === "Site Engineer";
  const isHO = HO_ROLES.includes(user?.role);
  const [piles, setPiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [formTarget, setFormTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = (pageNum = 1) => {
    setLoading(true);
    const params = { page: pageNum, page_size: 50 };
    if (search) params.search = search;
    if (isHO && siteFilter) params.site = siteFilter;
    const endpoint = isSE ? "/pile-records/my-piles" : "/pile-records";
    client
      .get(endpoint, { params })
      .then((res) => {
        setPiles(res.data.items);
        setTotalPages(res.data.total_pages);
        setPage(res.data.page);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(1), [siteFilter, search]);

  useEffect(() => {
    if (isHO) client.get("/pile-records/sites").then((res) => setSites(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHO]);

  const handleSaved = (saved) => {
    setFormTarget(null);
    setPiles((prev) => {
      const exists = prev.some((x) => x.id === saved.id);
      return exists ? prev.map((x) => (x.id === saved.id ? saved : x)) : [saved, ...prev];
    });
    setSelected(saved);
  };

  if (!isSE && !isHO) return <NoAccess />;

  return (
    <div className="space-y-4" data-testid="pile-record-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="pile-search-input" placeholder="Search Pile ID or Chainage" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {isHO && <SiteFilterSelect sites={sites} value={siteFilter} onChange={setSiteFilter} testId="pile-site-filter" placeholder="All sites" allowAll />}
        </div>
        {(isSE || isHO) && (
          <Button data-testid="pile-add-button" onClick={() => setFormTarget("new")} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Pile Record
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="pile-table">
          <TableHeader>
            <TableRow>
              <TableHead>Pile ID</TableHead>
              <TableHead>Chainage</TableHead>
              {isHO && <TableHead>Site</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead>Machine</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isHO ? 6 : 5} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : piles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isHO ? 6 : 5} className="text-center py-8 text-slate-400">
                  No pile records found.
                </TableCell>
              </TableRow>
            ) : (
              piles.map((p) => (
                <TableRow key={p.id} data-testid={`pile-row-${p.id}`} className="cursor-pointer" onClick={() => setSelected(p)}>
                  <TableCell className="font-mono text-xs font-semibold">
                    {p.pile_id_display}
                    {p.entered_by_office && (
                      <Badge
                        variant="outline"
                        className="ml-2 font-sans font-normal text-[10px] px-1.5 py-0 border-sky-300 text-sky-700 bg-sky-50"
                        data-testid={`pile-office-badge-${p.id}`}
                      >
                        Entered by Office
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{p.chainage}</TableCell>
                  {isHO && <TableCell className="text-xs">{p.site_display_label || p.site}</TableCell>}
                  <TableCell>
                    <Badge className={p.status === "Completed" ? "bg-emerald-600" : "bg-amber-500"}>{p.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">{p.machine_label || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{new Date(p.updated_at).toLocaleDateString("en-IN")}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="pile-pagination" />

      {selected && (
        <PileDetailPanel
          pile={selected}
          onClose={() => setSelected(null)}
          onEdit={(p) => {
            setSelected(null);
            setFormTarget(p);
          }}
          canDeactivate={isHO}
          onUpdated={(updated) => {
            if (updated.active === false) {
              setPiles((prev) => prev.filter((x) => x.id !== updated.id));
              setSelected(null);
            } else {
              setSelected(updated);
              setPiles((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            }
          }}
        />
      )}

      {formTarget && (
        <PileFormDialog
          pile={formTarget === "new" ? null : formTarget}
          onClose={() => setFormTarget(null)}
          onSaved={handleSaved}
          isOfficeRole={isHO}
          officeSites={sites}
        />
      )}
    </div>
  );
}
